import React, { useCallback, useEffect, useRef, useState } from 'react';
import './LoginPage.scss';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { PasswordInput } from '../../shared/components/common/password-input/PasswordInput';
import { Link, Location, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { signIn } from './services/LoginService';
import { AuthData } from './models/LoginFormData';
import { useAuth } from '../../shared/contexts/auth/AuthContext';
import { useTranslation } from 'react-i18next';
import { PROJECTS, PROJECTS_ADD } from '../../shared/routes/path';
import { REACT_APP_CLIENT_ID, REACT_APP_GOOGLE_CLIENT_ID } from '../../shared/utils/config';
import { authenticateWithGoogle, sendMagicLink } from '../../shared/services/auth/AuthService';
import { getAuthConfig, resendVerification } from '../signup/services/SignupService';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import googleLogo from '../../assets/images/google-logo.svg';

type LoginMode = 'password' | 'magic-link';

const LoginPage: React.FC = () => {
  const [apiError, setApiError] = useState<string | null>(null);
  const [mode, setMode] = useState<LoginMode>('password');
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkError, setMagicLinkError] = useState<string | null>(null);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [magicLinkCooldown, setMagicLinkCooldown] = useState(() => {
    const stored = localStorage.getItem('magicLinkCooldownUntil');
    if (stored) {
      const remaining = Math.ceil((parseInt(stored, 10) - Date.now()) / 1000);
      return remaining > 0 ? remaining : 0;
    }
    return 0;
  });
  const magicLinkCooldownRef = useRef<NodeJS.Timeout | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<NodeJS.Timeout | null>(null);
  const [verificationToast, setVerificationToast] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error' | 'warning'>('success');
  const [toastHiding, setToastHiding] = useState(false);
  const [fingerprint, setFingerprint] = useState('');
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const googleHiddenRef = useRef<HTMLDivElement | null>(null);
  const toastHideRef = useRef<NodeJS.Timeout | null>(null);
  const toastRemoveRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const emailVerifiedQuery = new URLSearchParams(location.search).get('emailVerified');
  const emailVerified =
    emailVerifiedQuery ?? (location.state as { emailVerified?: string })?.emailVerified;
  const fromLocation = (location.state as { from?: Location })?.from;
  const from = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search}${fromLocation.hash}`
    : PROJECTS;
  const { login } = useAuth();
  const { t, i18n } = useTranslation();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    document.body.classList.add('visible', 'white');
    getAuthConfig()
      .then((res) => {
        if (res.data.fingerprintProtection) {
          FingerprintJS.load()
            .then((fp) => fp.get())
            .then((result) => setFingerprint(result.visitorId));
        }
      })
      .catch(() => {});

    if (emailVerified === 'success') {
      setToastType('success');
      setVerificationToast(t('emailVerifiedSuccess'));
    } else if (emailVerified === 'error') {
      setToastType('error');
      setVerificationToast(t('emailVerificationFailed'));
    }

    if (emailVerified) {
      window.history.replaceState({}, '', location.pathname);
      toastHideRef.current = setTimeout(() => setToastHiding(true), 4700);
      toastRemoveRef.current = setTimeout(() => setVerificationToast(null), 5000);
      return () => {
        if (toastHideRef.current) clearTimeout(toastHideRef.current);
        if (toastRemoveRef.current) clearTimeout(toastRemoveRef.current);
      };
    }
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      return;
    }
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [resendCooldown > 0]);

  useEffect(() => {
    if (magicLinkCooldown <= 0) {
      if (magicLinkCooldownRef.current) clearInterval(magicLinkCooldownRef.current);
      return;
    }
    magicLinkCooldownRef.current = setInterval(() => {
      setMagicLinkCooldown((prev) => {
        if (prev <= 1) {
          if (magicLinkCooldownRef.current) clearInterval(magicLinkCooldownRef.current);
          localStorage.removeItem('magicLinkCooldownUntil');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (magicLinkCooldownRef.current) clearInterval(magicLinkCooldownRef.current);
    };
  }, [magicLinkCooldown > 0]);

  const handleGoogleCredential = useCallback(
    async (response: { credential?: string }) => {
      if (!response.credential) return;
      setGoogleError(null);
      try {
        const language = i18n.language?.split?.('-')?.[0] || i18n.language || 'en';
        const authData = await authenticateWithGoogle(response.credential, fingerprint, language);
        login(authData);
        navigate(authData.isNewAccount ? PROJECTS_ADD : from, { replace: true });
      } catch {
        setGoogleError(t('googleAuthFailed'));
      }
    },
    [fingerprint, login, navigate, from, t]
  );

  useEffect(() => {
    if (!REACT_APP_GOOGLE_CLIENT_ID) return;

    const initGoogle = () => {
      const google = (window as any).google;
      if (!google?.accounts?.id || !googleHiddenRef.current) return;

      google.accounts.id.initialize({
        client_id: REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        ux_mode: 'popup',
      });
      google.accounts.id.renderButton(googleHiddenRef.current, {
        type: 'icon',
        size: 'large',
      });
      setGoogleReady(true);
    };

    if ((window as any).google?.accounts?.id) {
      initGoogle();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [handleGoogleCredential]);

  const handleGoogleClick = useCallback(() => {
    const btn = googleHiddenRef.current?.querySelector('[role="button"]') as HTMLElement | null;
    if (btn) {
      btn.click();
    }
  }, []);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'warning') => {
    if (toastHideRef.current) clearTimeout(toastHideRef.current);
    if (toastRemoveRef.current) clearTimeout(toastRemoveRef.current);
    setToastHiding(false);
    setToastType(type);
    setVerificationToast(message);
    toastHideRef.current = setTimeout(() => setToastHiding(true), 4700);
    toastRemoveRef.current = setTimeout(() => setVerificationToast(null), 5000);
  }, []);

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || !unverifiedEmail) return;
    try {
      const response = await resendVerification(unverifiedEmail);
      const retryAfter = response?.data?.retryAfter || 600;
      setResendCooldown(retryAfter);
      localStorage.setItem('resendCooldownUntil', String(Date.now() + retryAfter * 1000));
      showToast(t('resendVerificationSuccess'), 'warning');
    } catch (err: any) {
      const retryAfter = err?.response?.data?.retryAfter;
      if (retryAfter) {
        setResendCooldown(retryAfter);
        localStorage.setItem('resendCooldownUntil', String(Date.now() + retryAfter * 1000));
      }
    }
  }, [resendCooldown, unverifiedEmail, showToast, t]);

  const onSubmit = async (loginFormData: any) => {
    const normalizedUsername = loginFormData.username.toLowerCase() || '';
    const data = {
      ...loginFormData,
      username: normalizedUsername,
      grant_type: 'password',
      client_id: REACT_APP_CLIENT_ID,
      browserData: JSON.stringify(navigator.userAgent),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      fingerprint,
    };

    const urlEncodedData = new URLSearchParams();
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        urlEncodedData.append(key, data[key as keyof AuthData]);
      }
    }

    await signIn(urlEncodedData.toString())
      .then((response) => {
        login(response.data);
        navigate(response.data.isNewAccount ? PROJECTS_ADD : from, { replace: true });
      })
      .catch((err) => {
        const errorCode = err.response?.data?.error;
        if (errorCode === 'EMAIL_NOT_VERIFIED') {
          setUnverifiedEmail(normalizedUsername);
          setApiError(t('emailNotVerified'));
          const stored = localStorage.getItem('resendCooldownUntil');
          if (stored) {
            const remaining = Math.ceil((parseInt(stored, 10) - Date.now()) / 1000);
            if (remaining > 0) {
              setResendCooldown(remaining);
            } else {
              localStorage.removeItem('resendCooldownUntil');
            }
          }
        } else {
          setApiError(errorCode);
        }
      });
  };

  const clearApiError = () => {
    setApiError(null);
    setUnverifiedEmail(null);
  };

  const onSendMagicLink = async () => {
    if (!magicLinkEmail || magicLinkEmail.trim().length === 0) {
      setMagicLinkError('Please enter your email address.');
      return;
    }

    setIsSendingMagicLink(true);
    setMagicLinkError(null);

    try {
      const language = i18n.language?.split?.('-')?.[0] || i18n.language || 'en';
      await sendMagicLink(magicLinkEmail.toLowerCase().trim(), fingerprint, language);
      showToast(t('magicLinkSent'), 'success');
      setMagicLinkEmail('');
      setMode('password');
      const cooldownSeconds = 600;
      setMagicLinkCooldown(cooldownSeconds);
      localStorage.setItem('magicLinkCooldownUntil', String(Date.now() + cooldownSeconds * 1000));
    } catch (err: any) {
      const retryAfter = err?.response?.data?.retryAfter;
      if (retryAfter) {
        setMagicLinkCooldown(retryAfter);
        localStorage.setItem('magicLinkCooldownUntil', String(Date.now() + retryAfter * 1000));
        showToast(t('tooManyAttempts'), 'error');
      } else {
        setMagicLinkError('Failed to send magic link. Please try again.');
      }
    } finally {
      setIsSendingMagicLink(false);
    }
  };

  const switchToMagicLink = () => {
    const currentEmail = getValues('username') || '';
    if (currentEmail) {
      setMagicLinkEmail(currentEmail);
    }
    setMagicLinkError(null);
    setMode('magic-link');
  };

  const switchToPassword = () => {
    setMagicLinkError(null);
    setMode('password');
  };

  return (
    <div className="login-page auth-pages">
      <div className="auth-pages__left">
        <div className="auth-pages__image">
          <div className="auth-pages__logo">
            <a href="https://chatbeat.com">
              <div className="logo-box" />
            </a>
          </div>
          <div className="auth-pages__desc" dangerouslySetInnerHTML={{ __html: t('cantWaitToShow') }} />
        </div>
      </div>
      <div className="auth-pages__right">
        <div className="auth-pages__belt">
          <div className="logo-box logo-box--second" />
          <Link to="/signup">{t('signUp')}</Link>
        </div>

        <div className="form-box">
          <div className="form-box__title">{t('logIn')}:</div>

          {mode === 'password' && (
            <form onSubmit={handleSubmit(onSubmit)} className="form-box__form">
              <div className="form-box__row">
                <div className="form-box__label">{t('emailAddress')}:</div>
                <InputText
                  onInput={clearApiError}
                  {...register('username', {
                    required: t('fieldIsRequired'),
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: t('wrongEmailAddress'),
                    },
                  })}
                  className={errors.username || apiError ? 'form-box__invalid' : ''}
                />
                {errors.username && <span>{errors.username.message as string}</span>}
              </div>

              <div className="form-box__row">
                <div className="form-box__label">{t('password')}:</div>
                <PasswordInput
                  {...register('password', { required: t('fieldIsRequired') })}
                  className={errors.password || apiError ? 'form-box__invalid' : ''}
                  onInput={clearApiError}
                />
                {errors.password && <span>{errors.password.message as string}</span>}
                {apiError && (
                  <span>
                    {apiError}
                    {unverifiedEmail && (
                      <>
                        {' '}
                        <button
                          type="button"
                          className="form-box__resend-link"
                          disabled={resendCooldown > 0}
                          onClick={handleResend}
                        >
                          {resendCooldown > 0
                            ? `${t('resendVerification')} (${Math.floor(resendCooldown / 60)}:${String(
                                resendCooldown % 60
                              ).padStart(2, '0')})`
                            : t('resendVerification')}
                        </button>
                      </>
                    )}
                  </span>
                )}
              </div>

              <Button className="btn" label={t('signIn')} type="submit" />

              <div className="form-box__row form-box__row--info">
                {t('forgotPassword')}? <Link to="/forgot">{t('resetNow')}</Link>
              </div>

              <div className="form-box__separator">
                <span>{t('or') ?? 'or'}</span>
              </div>

              <div className="form-box__row form-box__row--center">
                <Button
                  className="btn btn--secondary"
                  label={magicLinkCooldown > 0
                    ? `${t('sendMagicLinkInstead')} (${Math.floor(magicLinkCooldown / 60)}:${String(magicLinkCooldown % 60).padStart(2, '0')})`
                    : t('sendMagicLinkInstead')}
                  type="button"
                  disabled={magicLinkCooldown > 0}
                  onClick={switchToMagicLink}
                />
              </div>
            </form>
          )}

          {mode === 'magic-link' && (
            <div className="form-box__form">
              <div className="form-box__row">
                <div className="form-box__label">{t('emailAddress')}:</div>
                <InputText
                  type="email"
                  value={magicLinkEmail}
                  onChange={(e) => setMagicLinkEmail(e.target.value)}
                  className={magicLinkError ? 'form-box__invalid' : ''}
                />
                {magicLinkError && <span>{magicLinkError}</span>}
              </div>

              <Button
                className="btn"
                label={magicLinkCooldown > 0
                  ? `${t('sendMagicLink')} (${Math.floor(magicLinkCooldown / 60)}:${String(magicLinkCooldown % 60).padStart(2, '0')})`
                  : t('sendMagicLink')}
                type="button"
                disabled={isSendingMagicLink || magicLinkCooldown > 0}
                onClick={onSendMagicLink}
              />

              <div className="form-box__separator">
                <span>{t('or') ?? 'or'}</span>
              </div>

              <div className="form-box__row form-box__row--center">
                <Button
                  className="btn btn--secondary"
                  label={t('signInWithPassword')}
                  type="button"
                  onClick={switchToPassword}
                />
              </div>
            </div>
          )}

          {REACT_APP_GOOGLE_CLIENT_ID && googleReady && (
            <>
              <button type="button" className="form-box__google-btn" onClick={handleGoogleClick}>
                <img src={googleLogo} alt="Google" width="18" height="18" />
                <span>{t('signInWithGoogle')}</span>
              </button>
              {googleError && <span className="form-box__error">{googleError}</span>}
            </>
          )}
        </div>
      </div>
      <div ref={googleHiddenRef} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0, overflow: 'hidden' }} />
      {verificationToast && (
        <div className={`toast toast--verify toast--${toastType}${toastHiding ? ' toast--hiding' : ''}`}>
          {verificationToast}
        </div>
      )}
    </div>
  );
};

export default LoginPage;
